import React, { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  TextField,
  Typography
} from '@mui/material';
import { ClientCell } from '@/components/ClientCell';

export type TenantPickerItem = {
  tenantId: string;
  companyName?: string | undefined;
  logoUrl?: string | undefined;
};

type TenantPickerListProps = {
  tenants: TenantPickerItem[];
  excludeTenantIds?: string[];
  loading?: boolean;
  onSelect: (tenantId: string) => void;
  emptyMessage?: string;
  searchPlaceholder?: string;
};

const MIN_SEARCH_LEN = 3;

export const TenantPickerList: React.FC<TenantPickerListProps> = ({
  tenants,
  excludeTenantIds = [],
  loading = false,
  onSelect,
  emptyMessage = 'Nenhum cliente disponível.',
  searchPlaceholder = 'Buscar por nome ou ID do cliente'
}) => {
  const [search, setSearch] = useState('');

  const eligible = useMemo(
    () => tenants.filter(t => !excludeTenantIds.includes(t.tenantId)),
    [tenants, excludeTenantIds]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < MIN_SEARCH_LEN) return eligible;
    return eligible.filter(
      t => t.tenantId.toLowerCase().includes(q) || (t.companyName || '').toLowerCase().includes(q)
    );
  }, [eligible, search]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Carregando clientes...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label={searchPlaceholder}
        value={search}
        onChange={e => setSearch(e.target.value)}
        margin="normal"
        helperText={
          search.trim().length > 0 && search.trim().length < MIN_SEARCH_LEN
            ? `Digite pelo menos ${MIN_SEARCH_LEN} caracteres para filtrar`
            : eligible.length
              ? `${filtered.length} cliente(s) exibido(s)`
              : undefined
        }
      />
      <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
        {filtered.map(tenant => (
          <ListItem key={tenant.tenantId} disablePadding>
            <ListItemButton onClick={() => onSelect(tenant.tenantId)}>
              <ClientCell
                name={tenant.companyName || undefined}
                tenantId={tenant.tenantId}
                logoUrl={tenant.logoUrl}
                size="small"
              />
            </ListItemButton>
          </ListItem>
        ))}
        {filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1 }}>
            {search.trim().length >= MIN_SEARCH_LEN
              ? 'Nenhum cliente corresponde à busca.'
              : emptyMessage}
          </Typography>
        )}
      </List>
    </Box>
  );
};
